// frontend/src/components/admin/ProspectionHub.jsx
// ============================================================================
// PROSPECTION HUB v2 — Design SaaS Premium
// Pipeline Kanban + Fiche détail avec timeline
// ============================================================================

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Plus, Edit3, Trash2, Save, X, AlertCircle, CheckCircle2,
  RefreshCw, User, Phone, Mail, MapPin, Building2, FileText,
  ChevronRight, Users, Eye, ArrowLeft, ExternalLink,
  Upload, History, Clock, Send, FileCheck, MoreHorizontal,
  TrendingUp, Zap, Target, CheckSquare, Circle,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import superAdminApi from '../../services/superAdminApi';
import { API_URL as API_BASE_URL } from '../../config/apiBase';

// ============================================================================
// CONSTANTES & CONFIG
// ============================================================================
const STATUTS = [
  { value: 'nouveau',      label: 'Nouveau',       color: '#64748b', bg: '#f8fafc', border: '#e2e8f0', dot: '#94a3b8' },
  { value: 'contacte',     label: 'Contacté',      color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe', dot: '#3b82f6' },
  { value: 'devis_envoye', label: 'Devis envoyé',  color: '#d97706', bg: '#fffbeb', border: '#fde68a', dot: '#f59e0b' },
  { value: 'negociation',  label: 'Négociation',   color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe', dot: '#8b5cf6' },
  { value: 'converti',     label: 'Converti',      color: '#059669', bg: '#f0fdf4', border: '#a7f3d0', dot: '#10b981' },
  { value: 'perdu',        label: 'Perdu',         color: '#dc2626', bg: '#fef2f2', border: '#fecaca', dot: '#ef4444' },
];

const SECTEURS = [
  'Commerce', 'BTP / Construction', 'Éducation', 'Santé', 'Transport / Logistique',
  'Industrie', 'Hôtellerie / Restauration', 'Agriculture', 'Finance / Assurance',
  'Services', 'ONG / Association', 'Autre',
];

const TYPE_DOCS = [
  { value: 'devis_signe',         label: 'Devis signé',          icon: '✍️' },
  { value: 'fiche_inscription',   label: "Fiche d'inscription",  icon: '📋' },
  { value: 'piece_identite',      label: 'Pièce d\'identité',    icon: '🪪' },
  { value: 'document_entreprise', label: 'Document entreprise',  icon: '🏢' },
  { value: 'autre',               label: 'Autre document',        icon: '📎' },
];

const ACTION_CONFIG = {
  fiche_envoyee:  { icon: '📄', color: '#2563eb', label: 'Fiche envoyée' },
  devis_cree:     { icon: '📋', color: '#d97706', label: 'Devis créé' },
  devis_envoye:   { icon: '📤', color: '#d97706', label: 'Devis envoyé' },
  devis_accepte:  { icon: '✅', color: '#059669', label: 'Devis accepté' },
  contrat_cree:   { icon: '📝', color: '#7c3aed', label: 'Contrat créé' },
  document_recu:  { icon: '📎', color: '#0891b2', label: 'Document reçu' },
  statut_change:  { icon: '🔄', color: '#64748b', label: 'Statut modifié' },
  note:           { icon: '💬', color: '#64748b', label: 'Note' },
};

const getStatut = (v) => STATUTS.find(s => s.value === v) || STATUTS[0];
const fmt = (n) => parseFloat(n || 0).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' FCFA';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fmtTime = (d) => d ? new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

// ============================================================================
// COMPOSANT : StatusBadge
// ============================================================================
const StatusBadge = ({ statut, size = 'sm' }) => {
  const s = getStatut(statut);
  const sizes = { sm: { fontSize: 10, padding: '2px 8px' }, md: { fontSize: 12, padding: '4px 12px' } };
  return (
    <span style={{ ...sizes[size], fontWeight: 700, borderRadius: 99,
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
      display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.dot, flexShrink: 0 }} />
      {s.label}
    </span>
  );
};

// ============================================================================
// MODAL PROSPECT
// ============================================================================
const ProspectModal = ({ prospect, onSave, onClose, theme: t }) => {
  const isEdit = !!prospect;
  const [form, setForm] = useState({
    nom:         prospect?.nom         || '',
    contact:     prospect?.contact     || '',
    telephone:   prospect?.telephone   || '',
    email:       prospect?.email       || '',
    ville:       prospect?.ville       || '',
    pays:        prospect?.pays        || 'Congo',
    secteur:     prospect?.secteur     || '',
    nb_employes: prospect?.nb_employes || '',
    statut:      prospect?.statut      || 'nouveau',
    notes:       prospect?.notes       || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const inp = (extra = {}) => ({
    width: '100%', padding: '10px 14px', borderRadius: 10,
    background: t.bgInput, border: `1.5px solid ${t.border}`,
    color: t.text, fontSize: 13, boxSizing: 'border-box', outline: 'none',
    transition: 'border-color 0.15s', fontFamily: 'inherit', ...extra,
  });

  const handleSubmit = async () => {
    if (!form.nom.trim()) { setError('Le nom est requis'); return; }
    setSaving(true); setError('');
    try { await onSave(form); }
    catch { setError('Erreur lors de la sauvegarde'); setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      backdropFilter: 'blur(4px)' }}>
      <div style={{ width: '100%', maxWidth: 540, borderRadius: 20, background: t.bgCard,
        border: `1px solid ${t.border}`, display: 'flex', flexDirection: 'column',
        maxHeight: '90vh', overflow: 'hidden',
        boxShadow: '0 25px 50px rgba(0,0,0,0.25)' }}>

        {/* Header */}
        <div style={{ padding: '20px 24px 18px', borderBottom: `1px solid ${t.border}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <p style={{ fontSize: 16, fontWeight: 800, color: t.text, margin: '0 0 3px', letterSpacing: '-0.3px' }}>
                {isEdit ? 'Modifier le prospect' : 'Nouveau prospect'}
              </p>
              <p style={{ fontSize: 12, color: t.textMuted, margin: 0 }}>
                {isEdit ? prospect.nom : 'Ajouter un contact à votre pipeline'}
              </p>
            </div>
            <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: 8,
              border: `1px solid ${t.border}`, background: t.bgInput, color: t.textMuted,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.15s' }}>
              <X size={15} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {error && (
            <div style={{ padding: '10px 14px', borderRadius: 10,
              background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.15)',
              display: 'flex', gap: 8, alignItems: 'center' }}>
              <AlertCircle size={14} style={{ color: '#dc2626', flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: '#dc2626' }}>{error}</span>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div style={{ gridColumn: 'span 2' }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: t.textSecondary, display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Nom de l'entreprise *
              </label>
              <input value={form.nom} onChange={e => set('nom', e.target.value)}
                placeholder="BTP GROUP SARL" style={inp()}
                onFocus={e => e.target.style.borderColor = t.primary}
                onBlur={e  => e.target.style.borderColor = t.border} />
            </div>

            {[
              { key: 'contact',    label: 'Interlocuteur',  placeholder: 'Jean Dupont',          col: 1 },
              { key: 'telephone',  label: 'Téléphone',       placeholder: '+242 06 xxx xxxx',     col: 1 },
              { key: 'email',      label: 'Email',           placeholder: 'contact@entreprise.com', col: 1 },
              { key: 'ville',      label: 'Ville',           placeholder: 'Brazzaville',           col: 1 },
            ].map(f => (
              <div key={f.key}>
                <label style={{ fontSize: 11, fontWeight: 700, color: t.textSecondary, display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{f.label}</label>
                <input value={form[f.key]} onChange={e => set(f.key, e.target.value)}
                  placeholder={f.placeholder} style={inp()}
                  onFocus={e => e.target.style.borderColor = t.primary}
                  onBlur={e  => e.target.style.borderColor = t.border} />
              </div>
            ))}

            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: t.textSecondary, display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Secteur</label>
              <select value={form.secteur} onChange={e => set('secteur', e.target.value)} style={inp()}>
                <option value="">— Choisir —</option>
                {SECTEURS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: t.textSecondary, display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Nb. employés</label>
              <input type="number" min={1} value={form.nb_employes}
                onChange={e => set('nb_employes', e.target.value)}
                placeholder="25" style={inp()}
                onFocus={e => e.target.style.borderColor = t.primary}
                onBlur={e  => e.target.style.borderColor = t.border} />
            </div>

            {isEdit && (
              <div style={{ gridColumn: 'span 2' }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: t.textSecondary, display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Statut</label>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {STATUTS.map(s => (
                    <button key={s.value} onClick={() => set('statut', s.value)}
                      style={{ padding: '5px 12px', borderRadius: 99, cursor: 'pointer', fontSize: 12,
                        fontWeight: form.statut === s.value ? 700 : 500, border: `1.5px solid`,
                        borderColor: form.statut === s.value ? s.color : t.border,
                        background: form.statut === s.value ? s.bg : t.bgInput,
                        color: form.statut === s.value ? s.color : t.textMuted }}>
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div style={{ gridColumn: 'span 2' }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: t.textSecondary, display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Notes</label>
              <textarea value={form.notes} onChange={e => set('notes', e.target.value)}
                rows={3} placeholder="Contexte, besoins spécifiques..."
                style={inp({ resize: 'vertical', lineHeight: '1.5' })}
                onFocus={e => e.target.style.borderColor = t.primary}
                onBlur={e  => e.target.style.borderColor = t.border} />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: `1px solid ${t.border}`,
          display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={{ padding: '10px 20px', borderRadius: 10,
            background: t.bgInput, border: `1px solid ${t.border}`, color: t.textSecondary,
            fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            Annuler
          </button>
          <button onClick={handleSubmit} disabled={saving}
            style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '10px 22px',
              borderRadius: 10, background: t.primary, border: 'none', color: 'white',
              fontSize: 13, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.7 : 1, boxShadow: `0 4px 12px ${t.primary}40` }}>
            {saving
              ? <span style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} />
              : <Save size={14} />}
            {saving ? 'Enregistrement...' : isEdit ? 'Mettre à jour' : 'Créer le prospect'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// COMPOSANT : Carte Kanban
// ============================================================================
const KanbanCard = ({ prospect, onClick, theme: t }) => {
  const [hovered, setHovered] = useState(false);
  let fiches = []; try { fiches = JSON.parse(prospect.fiches_envoyees || '[]'); } catch {}
  let docs   = []; try { docs   = JSON.parse(prospect.documents_recus  || '[]'); } catch {}

  return (
    <div onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: '14px 16px', borderRadius: 14, cursor: 'pointer',
        background: hovered ? t.bgCardHover || t.bgCard : t.bgCard,
        border: `1px solid ${hovered ? t.borderActive || t.primary + '40' : t.border}`,
        transition: 'all 0.18s cubic-bezier(0.4,0,0.2,1)',
        transform: hovered ? 'translateY(-2px)' : 'none',
        boxShadow: hovered ? '0 8px 24px rgba(0,0,0,0.1)' : '0 1px 3px rgba(0,0,0,0.04)',
        marginBottom: 10,
      }}>

      {/* Nom + arrow */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <div style={{ width: 34, height: 34, borderRadius: 9, flexShrink: 0,
            background: `linear-gradient(135deg, ${t.primary}20, ${t.primary}10)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: `1px solid ${t.primary}20` }}>
            <Building2 size={15} style={{ color: t.primary }} />
          </div>
          <div style={{ minWidth: 0 }}>
            <p style={{ fontSize: 13, fontWeight: 700, color: t.text, margin: 0,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              letterSpacing: '-0.2px' }}>{prospect.nom}</p>
            {prospect.contact && (
              <p style={{ fontSize: 11, color: t.textMuted, margin: 0 }}>{prospect.contact}</p>
            )}
          </div>
        </div>
        <ChevronRight size={14} style={{ color: hovered ? t.primary : t.textMuted,
          transition: 'color 0.15s', flexShrink: 0, marginTop: 2 }} />
      </div>

      {/* Meta */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
        {prospect.ville && (
          <span style={{ fontSize: 10, color: t.textMuted, display: 'flex', alignItems: 'center', gap: 3 }}>
            <MapPin size={9} />{prospect.ville}
          </span>
        )}
        {prospect.nb_employes && (
          <span style={{ fontSize: 10, color: t.textMuted }}>· {prospect.nb_employes} emp.</span>
        )}
      </div>

      {/* Badges activité */}
      {(prospect.nb_devis > 0 || fiches.length > 0 || docs.length > 0) && (
        <div style={{ display: 'flex', gap: 5, marginTop: 8, flexWrap: 'wrap' }}>
          {fiches.length > 0 && (
            <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 6,
              background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe' }}>
              📄 {fiches.length} fiche{fiches.length > 1 ? 's' : ''}
            </span>
          )}
          {prospect.nb_devis > 0 && (
            <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 6,
              background: '#fffbeb', color: '#d97706', border: '1px solid #fde68a' }}>
              📋 {prospect.nb_devis} devis
            </span>
          )}
          {docs.length > 0 && (
            <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 6,
              background: '#f0f9ff', color: '#0891b2', border: '1px solid #bae6fd' }}>
              📎 {docs.length} doc{docs.length > 1 ? 's' : ''}
            </span>
          )}
        </div>
      )}

      {/* Dernière activité */}
      {prospect.date_dernier_contact && (
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${t.border}`,
          display: 'flex', alignItems: 'center', gap: 4 }}>
          <Clock size={9} style={{ color: t.textMuted }} />
          <span style={{ fontSize: 9, color: t.textMuted }}>{fmtDate(prospect.date_dernier_contact)}</span>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// COMPOSANT : Fiche détail prospect
// ============================================================================
const ProspectDetail = ({ prospectId, onBack, onEdit, onCreateDevis, onViewContrat, theme: t, onReload }) => {
  const [prospect,   setProspect]   = useState(null);
  const [actions,    setActions]    = useState([]);
  const [devis,      setDevis]      = useState([]);
  const [contrats,   setContrats]   = useState([]);
  const [docsList,   setDocsList]   = useState([]);
  const [fichesList, setFichesList] = useState([]);
  const [onglet,     setOnglet]     = useState('fiches');
  const [loading,    setLoading]    = useState(true);
  const [uploading,  setUploading]  = useState(false);
  const [note,       setNote]       = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [markingSent,setMarkingSent]= useState(null);
  const [editModal,  setEditModal]  = useState(false);
  const API_URL = API_BASE_URL;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, aRes, dRes, cRes] = await Promise.all([
        superAdminApi.prospects.getOne(prospectId),
        superAdminApi.prospects.getActions(prospectId),
        superAdminApi.devis.getAll(),
        superAdminApi.contrats.getAll(),
      ]);
      if (pRes.success) {
        const p = pRes.data;
        setProspect(p);
        // Parser JSON ici pour éviter les problèmes de re-render
        try { setDocsList(JSON.parse(p.documents_recus || '[]')); } catch { setDocsList([]); }
        try { setFichesList(JSON.parse(p.fiches_envoyees || '[]')); } catch { setFichesList([]); }
      }
      if (aRes.success) setActions(aRes.data || []);
      if (dRes.success) setDevis((dRes.data || []).filter(d => d.prospect_id === prospectId));
      if (cRes.success) setContrats((cRes.data || []).filter(c => c.prospect_id === prospectId));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [prospectId]);

  useEffect(() => { load(); }, [load]);

  const handleMarkFicheEnvoyee = async (type) => {
    setMarkingSent(type);
    try {
      await superAdminApi.prospects.logAction(prospectId, 'fiche_envoyee',
        `Fiche ${type === 'presentation' ? 'Présentation' : 'Licences & Tarifs'} envoyée au prospect`,
        { type_fiche: type }
      );
      // Mettre à jour immédiatement le state local pour que le bouton change
      setFichesList(prev => prev.includes(type) ? prev : [...prev, type]);
      // Mettre à jour statut si nouveau
      if (prospect?.statut === 'nouveau') {
        await superAdminApi.prospects.updateStatut(prospectId, 'contacte');
        setProspect(p => ({ ...p, statut: 'contacte' }));
      }
      await load();
      if (onReload) onReload();
    } finally { setMarkingSent(null); }
  };

  const handleUploadDoc = async (e, typeDoc) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(typeDoc);
    try {
      const res = await superAdminApi.prospects.uploadDocument(prospectId, file, typeDoc, file.name);
      // Mettre à jour immédiatement les docs
      if (res?.docs) {
        setDocsList(res.docs);
      }
      await load();
    } catch (err) { console.error(err); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleDeleteDoc = async (path) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    await superAdminApi.prospects.deleteDocument(prospectId, path);
    await load();
  };

  const handleAddNote = async () => {
    if (!note.trim()) return;
    setSavingNote(true);
    try {
      await superAdminApi.prospects.logAction(prospectId, 'note', note);
      setNote('');
      await load();
    } finally { setSavingNote(false); }
  };

  if (loading || !prospect) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 80, gap: 12 }}>
      <RefreshCw size={18} style={{ color: t.primary, animation: 'spin 1s linear infinite' }} />
      <span style={{ fontSize: 13, color: t.textMuted }}>Chargement…</span>
    </div>
  );

  const sc = getStatut(prospect.statut);
  let fiches = []; try { fiches = JSON.parse(prospect.fiches_envoyees || '[]'); } catch {}
  let docs   = []; try { docs   = JSON.parse(prospect.documents_recus  || '[]'); } catch {}

  // Onglets avec étapes du pipeline
  const ONGLETS = [
    { key: 'fiches',     label: 'Fiches',      icon: Send,      step: 1, done: fiches.length > 0 },
    { key: 'devis',      label: 'Devis',       icon: FileText,  step: 2, done: devis.length > 0, count: devis.length },
    { key: 'documents',  label: 'Documents',   icon: Upload,    step: 3, done: docs.length > 0,  count: docs.length },
    { key: 'contrat',    label: 'Contrat',     icon: FileCheck, step: 4, done: contrats.length > 0, count: contrats.length },
    { key: 'historique', label: 'Historique',  icon: History,   step: null, count: actions.length },
  ];

  const handleSaveEdit = async (form) => {
    const res = await superAdminApi.prospects.update(prospectId, form);
    if (!res.success) throw new Error(res.message);
    setEditModal(false);
    await load();
    if (onReload) onReload();
  };

  return (
    <>
    {editModal && (
      <ProspectModal
        prospect={prospect}
        onSave={handleSaveEdit}
        onClose={() => setEditModal(false)}
        theme={t}
      />
    )}
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>

      {/* ── Header ── */}
      <div style={{ padding: '20px 28px', borderBottom: `1px solid ${t.border}`,
        display: 'flex', alignItems: 'center', gap: 16, background: t.bgCard }}>
        <button onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: 6,
          padding: '8px 14px', borderRadius: 10, background: t.bgInput,
          border: `1px solid ${t.border}`, color: t.textSecondary,
          fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
          <ArrowLeft size={13} /> Retour
        </button>

        {/* Avatar + Infos */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 0 }}>
          <div style={{ width: 48, height: 48, borderRadius: 14, flexShrink: 0,
            background: `linear-gradient(135deg, ${t.primary}25, ${t.primary}10)`,
            border: `1.5px solid ${t.primary}30`,
            display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Building2 size={22} style={{ color: t.primary }} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <h2 style={{ fontSize: 17, fontWeight: 800, color: t.text, margin: 0, letterSpacing: '-0.4px' }}>
                {prospect.nom}
              </h2>
              <StatusBadge statut={prospect.statut} size="sm" />
            </div>
            <div style={{ display: 'flex', gap: 12, marginTop: 2, flexWrap: 'wrap' }}>
              {prospect.contact   && <span style={{ fontSize: 11, color: t.textMuted, display: 'flex', alignItems: 'center', gap: 4 }}><User size={10} />{prospect.contact}</span>}
              {prospect.telephone && <span style={{ fontSize: 11, color: t.textMuted, display: 'flex', alignItems: 'center', gap: 4 }}><Phone size={10} />{prospect.telephone}</span>}
              {prospect.ville     && <span style={{ fontSize: 11, color: t.textMuted, display: 'flex', alignItems: 'center', gap: 4 }}><MapPin size={10} />{prospect.ville}</span>}
            </div>
          </div>
        </div>

        {/* Actions rapides */}
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <button onClick={() => setEditModal(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
              borderRadius: 10, background: t.bgInput, color: t.textSecondary,
              border: `1px solid ${t.border}`, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            <Edit3 size={13} /> Modifier
          </button>
          {prospect.statut !== 'converti' && (
            <button onClick={() => onCreateDevis(prospect)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px',
                borderRadius: 10, background: t.primary, color: 'white', border: 'none',
                fontSize: 12, fontWeight: 700, cursor: 'pointer',
                boxShadow: `0 4px 12px ${t.primary}35` }}>
              <Plus size={13} /> Nouveau devis
            </button>
          )}
        </div>
      </div>

      {/* ── Pipeline Steps ── */}
      <div style={{ padding: '14px 28px', borderBottom: `1px solid ${t.border}`,
        background: t.bgInput, display: 'flex', alignItems: 'center', gap: 0, overflowX: 'auto' }}>
        {ONGLETS.filter(o => o.step).map((o, i, arr) => {
          const isActive = onglet === o.key;
          const isDone   = o.done;
          return (
            <div key={o.key} style={{ display: 'flex', alignItems: 'center' }}>
              <button onClick={() => setOnglet(o.key)} style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px',
                borderRadius: 99, border: `1.5px solid`,
                borderColor: isActive ? t.primary : isDone ? '#10b981' : t.border,
                background: isActive ? t.primarySoft : isDone ? '#f0fdf4' : 'transparent',
                color: isActive ? t.primary : isDone ? '#059669' : t.textMuted,
                fontSize: 12, fontWeight: isActive ? 700 : 500, cursor: 'pointer',
                transition: 'all 0.15s', whiteSpace: 'nowrap',
              }}>
                <div style={{ width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: isActive ? t.primary : isDone ? '#10b981' : t.border,
                  color: 'white', fontSize: 9, fontWeight: 800 }}>
                  {isDone && !isActive ? '✓' : o.step}
                </div>
                {o.label}
                {o.count > 0 && (
                  <span style={{ fontSize: 9, fontWeight: 800, padding: '1px 5px',
                    borderRadius: 99, background: isActive ? t.primary : isDone ? '#10b981' : t.bgCard,
                    color: (isActive || isDone) ? 'white' : t.textMuted }}>
                    {o.count}
                  </span>
                )}
              </button>
              {i < arr.length - 1 && (
                <div style={{ width: 28, height: 1, background: isDone ? '#10b981' : t.border, margin: '0 2px' }} />
              )}
            </div>
          );
        })}
        <div style={{ marginLeft: 'auto', paddingLeft: 16 }}>
          <button onClick={() => setOnglet('historique')}
            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px',
              borderRadius: 99, border: `1px solid ${onglet === 'historique' ? t.primary : t.border}`,
              background: onglet === 'historique' ? t.primarySoft : 'transparent',
              color: onglet === 'historique' ? t.primary : t.textMuted,
              fontSize: 11, fontWeight: 500, cursor: 'pointer' }}>
            <History size={12} /> Historique
            {actions.length > 0 && (
              <span style={{ fontSize: 9, fontWeight: 800, padding: '1px 5px', borderRadius: 99,
                background: t.bgCard, color: t.textMuted }}>{actions.length}</span>
            )}
          </button>
        </div>
      </div>

      {/* ── Contenu ── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 28 }}>

        {/* FICHES */}
        {onglet === 'fiches' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 680 }}>
            <div style={{ padding: '14px 18px', borderRadius: 14, background: `${t.primary}08`,
              border: `1px solid ${t.primary}20`, display: 'flex', gap: 10 }}>
              <Target size={16} style={{ color: t.primary, flexShrink: 0, marginTop: 1 }} />
              <div>
                <p style={{ fontSize: 13, fontWeight: 700, color: t.text, margin: '0 0 2px' }}>Étape 1 — Envoi des fiches de prospection</p>
                <p style={{ fontSize: 12, color: t.textMuted, margin: 0 }}>
                  Téléchargez les fiches depuis le module Prospection et envoyez-les au prospect.
                  Cliquez sur <strong>Marquer comme envoyée</strong> pour enregistrer la trace.
                </p>
              </div>
            </div>

            {[
              { type: 'presentation',    label: 'Fiche Présentation',     desc: 'Présentation générale HoraPilot — design commercial A4 paysage', icon: '🎨' },
              { type: 'licences_tarifs', label: 'Fiche Licences & Tarifs', desc: 'Comparatif des licences, tarifs, modules inclus, add-ons',        icon: '💰' },
            ].map(fiche => {
              const envoyee = fiches.includes(fiche.type);
              const sending = markingSent === fiche.type;
              return (
                <div key={fiche.type} style={{
                  padding: '18px 20px', borderRadius: 16, background: t.bgCard,
                  border: `1.5px solid ${envoyee ? '#a7f3d0' : t.border}`,
                  display: 'flex', alignItems: 'center', gap: 16,
                  transition: 'border-color 0.2s',
                }}>
                  <div style={{ width: 46, height: 46, borderRadius: 12, flexShrink: 0,
                    background: envoyee ? '#f0fdf4' : t.bgInput,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 20, border: `1.5px solid ${envoyee ? '#a7f3d0' : t.border}` }}>
                    {fiche.icon}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                      <p style={{ fontSize: 14, fontWeight: 700, color: t.text, margin: 0 }}>{fiche.label}</p>
                      {envoyee && (
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
                          background: '#f0fdf4', color: '#059669', border: '1px solid #a7f3d0' }}>
                          ✓ Envoyée
                        </span>
                      )}
                    </div>
                    <p style={{ fontSize: 12, color: t.textMuted, margin: 0 }}>{fiche.desc}</p>
                  </div>
                  <button onClick={() => handleMarkFicheEnvoyee(fiche.type)} disabled={sending}
                    style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 18px',
                      borderRadius: 10, border: `1.5px solid`,
                      borderColor: envoyee ? '#a7f3d0' : t.primary,
                      background: envoyee ? '#f0fdf4' : t.primarySoft,
                      color: envoyee ? '#059669' : t.primary,
                      fontSize: 12, fontWeight: 700, cursor: sending ? 'wait' : 'pointer',
                      opacity: sending ? 0.7 : 1, whiteSpace: 'nowrap' }}>
                    {sending
                      ? <span style={{ width: 13, height: 13, border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} />
                      : envoyee ? <CheckCircle2 size={13} /> : <Send size={13} />}
                    {envoyee ? 'Marquer de nouveau' : 'Marquer comme envoyée'}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* DEVIS */}
        {onglet === 'devis' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 720 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <p style={{ fontSize: 15, fontWeight: 800, color: t.text, margin: '0 0 3px' }}>Devis</p>
                <p style={{ fontSize: 12, color: t.textMuted, margin: 0 }}>{devis.length} devis associé{devis.length !== 1 ? 's' : ''}</p>
              </div>
              <button onClick={() => onCreateDevis(prospect)}
                style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 18px',
                  borderRadius: 10, background: t.primary, color: 'white', border: 'none',
                  fontSize: 13, fontWeight: 700, cursor: 'pointer', boxShadow: `0 4px 12px ${t.primary}35` }}>
                <Plus size={14} /> Nouveau devis
              </button>
            </div>
            {devis.length === 0 ? (
              <div style={{ padding: 60, textAlign: 'center', borderRadius: 16, background: t.bgCard,
                border: `2px dashed ${t.border}` }}>
                <FileText size={36} style={{ color: t.textMuted, opacity: 0.3, margin: '0 auto 12px', display: 'block' }} />
                <p style={{ fontSize: 14, fontWeight: 600, color: t.textMuted }}>Aucun devis créé</p>
                <p style={{ fontSize: 12, color: t.textMuted, marginTop: 4 }}>Créez un devis personnalisé pour ce prospect</p>
              </div>
            ) : devis.map(d => {
              const STATUT_D = {
                brouillon: { c: '#64748b', bg: '#f8fafc', border: '#e2e8f0' },
                envoye:    { c: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
                accepte:   { c: '#059669', bg: '#f0fdf4', border: '#a7f3d0' },
                refuse:    { c: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
                expire:    { c: '#d97706', bg: '#fffbeb', border: '#fde68a' },
              };
              const sd = STATUT_D[d.statut] || STATUT_D.brouillon;
              return (
                <div key={d.id} style={{ padding: '16px 20px', borderRadius: 14, background: t.bgCard,
                  border: `1px solid ${t.border}`, display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{ width: 42, height: 42, borderRadius: 11, flexShrink: 0,
                    background: sd.bg, border: `1px solid ${sd.border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <FileText size={18} style={{ color: sd.c }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                      <p style={{ fontSize: 13, fontWeight: 800, color: t.text, margin: 0 }}>{d.numero}</p>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
                        background: sd.bg, color: sd.c, border: `1px solid ${sd.border}` }}>{d.statut}</span>
                      {d.plan_label && <span style={{ fontSize: 10, color: t.textMuted }}>{d.plan_label}</span>}
                    </div>
                    <p style={{ fontSize: 12, color: t.textMuted, margin: 0 }}>
                      <strong style={{ color: t.text }}>{fmt(d.montant_final)}/mois</strong>
                      {d.expires_at && ` · Expire le ${fmtDate(d.expires_at)}`}
                    </p>
                  </div>
                  <button onClick={() => superAdminApi.devis.getPdf(d.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px',
                      borderRadius: 9, background: t.bgInput, color: t.text,
                      border: `1px solid ${t.border}`, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    <Eye size={13} /> PDF
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* DOCUMENTS */}
        {onglet === 'documents' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 680 }}>
            <div style={{ padding: '14px 18px', borderRadius: 14, background: '#f0f9ff',
              border: '1px solid #bae6fd', display: 'flex', gap: 10 }}>
              <Upload size={16} style={{ color: '#0891b2', flexShrink: 0, marginTop: 1 }} />
              <div>
                <p style={{ fontSize: 13, fontWeight: 700, color: '#0c4a6e', margin: '0 0 2px' }}>Étape 3 — Documents reçus du prospect</p>
                <p style={{ fontSize: 12, color: '#0369a1', margin: 0 }}>
                  Uploadez les documents que le prospect vous a renvoyés (devis signé, fiche d'inscription, etc.)
                </p>
              </div>
            </div>

            {/* Zones d'upload groupées par type avec docs associés */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {TYPE_DOCS.map(td => {
                const docsForType = docs.filter(d => d.type === td.value);
                const isUploading = uploading === td.value;
                const BASE_URL = API_BASE_URL;
                return (
                  <div key={td.value} style={{ borderRadius: 14, background: t.bgCard,
                    border: `1.5px solid ${docsForType.length > 0 ? '#bae6fd' : t.border}`,
                    overflow: 'hidden', transition: 'border-color 0.2s' }}>
                    {/* Zone upload */}
                    <label style={{ display: 'flex', alignItems: 'center', gap: 12,
                      padding: '12px 16px', cursor: 'pointer',
                      opacity: uploading && !isUploading ? 0.5 : 1,
                      background: isUploading ? `${t.primary}06` : 'transparent' }}>
                      <span style={{ fontSize: 22, flexShrink: 0 }}>{td.icon}</span>
                      <div style={{ flex: 1 }}>
                        <p style={{ fontSize: 13, fontWeight: 700, color: t.text, margin: '0 0 2px' }}>{td.label}</p>
                        <p style={{ fontSize: 11, color: t.textMuted, margin: 0 }}>
                          {isUploading ? '⏳ Upload en cours...' : docsForType.length > 0
                            ? `✓ ${docsForType.length} fichier${docsForType.length > 1 ? 's' : ''} — cliquer pour en ajouter`
                            : 'Cliquer pour uploader (PDF, JPG, PNG, DOC)'}
                        </p>
                      </div>
                      {docsForType.length > 0 && (
                        <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px',
                          borderRadius: 99, background: '#f0f9ff', color: '#0891b2',
                          border: '1px solid #bae6fd', flexShrink: 0 }}>
                          {docsForType.length} fichier{docsForType.length > 1 ? 's' : ''}
                        </span>
                      )}
                      <div style={{ width: 32, height: 32, borderRadius: 9, flexShrink: 0,
                        background: docsForType.length > 0 ? '#f0f9ff' : t.bgInput,
                        border: `1px solid ${docsForType.length > 0 ? '#bae6fd' : t.border}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Upload size={14} style={{ color: docsForType.length > 0 ? '#0891b2' : t.textMuted }} />
                      </div>
                      <input type="file" style={{ display: 'none' }}
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                        onChange={e => handleUploadDoc(e, td.value)}
                        disabled={!!uploading} />
                    </label>

                    {/* Documents uploadés pour ce type */}
                    {docsForType.length > 0 && (
                      <div style={{ borderTop: `1px solid ${t.border}`, padding: '8px 16px 10px' }}>
                        {docsForType.map((doc, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10,
                            padding: '6px 0', borderBottom: i < docsForType.length - 1 ? `1px solid ${t.border}` : 'none' }}>
                            <FileText size={14} style={{ color: '#0891b2', flexShrink: 0 }} />
                            <a href={`${BASE_URL}${doc.path}`} target="_blank" rel="noreferrer"
                              style={{ flex: 1, fontSize: 12, fontWeight: 600, color: t.primary,
                                textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {doc.filename || doc.label}
                            </a>
                            <span style={{ fontSize: 10, color: t.textMuted, flexShrink: 0 }}>
                              {fmtDate(doc.uploaded_at)}
                            </span>
                            <button onClick={() => handleDeleteDoc(doc.path)}
                              style={{ width: 24, height: 24, borderRadius: 6, flexShrink: 0,
                                background: 'rgba(220,38,38,0.06)', color: '#dc2626',
                                border: '1px solid rgba(220,38,38,0.12)',
                                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Trash2 size={11} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* CONTRAT */}
        {onglet === 'contrat' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 720 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <p style={{ fontSize: 15, fontWeight: 800, color: t.text, margin: '0 0 3px' }}>Contrats</p>
                <p style={{ fontSize: 12, color: t.textMuted, margin: 0 }}>{contrats.length} contrat{contrats.length !== 1 ? 's' : ''}</p>
              </div>
            </div>
            {contrats.length === 0 ? (
              <div style={{ padding: 60, textAlign: 'center', borderRadius: 16, background: t.bgCard, border: `2px dashed ${t.border}` }}>
                <FileCheck size={36} style={{ color: t.textMuted, opacity: 0.3, margin: '0 auto 12px', display: 'block' }} />
                <p style={{ fontSize: 14, fontWeight: 600, color: t.textMuted }}>Aucun contrat généré</p>
                <p style={{ fontSize: 12, color: t.textMuted, marginTop: 4 }}>Acceptez un devis pour générer le contrat automatiquement</p>
              </div>
            ) : contrats.map(c => {
              const STATUT_C = { brouillon: { c: '#64748b', bg: '#f8fafc', border: '#e2e8f0' }, signe: { c: '#059669', bg: '#f0fdf4', border: '#a7f3d0' }, resilie: { c: '#dc2626', bg: '#fef2f2', border: '#fecaca' } };
              const sc2 = STATUT_C[c.statut] || STATUT_C.brouillon;
              return (
                <div key={c.id} style={{ padding: '16px 20px', borderRadius: 14, background: t.bgCard,
                  border: `1px solid ${c.statut === 'signe' ? '#a7f3d0' : t.border}`,
                  display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{ width: 42, height: 42, borderRadius: 11, flexShrink: 0,
                    background: sc2.bg, border: `1px solid ${sc2.border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <FileCheck size={18} style={{ color: sc2.c }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                      <p style={{ fontSize: 13, fontWeight: 800, color: t.text, margin: 0 }}>{c.numero}</p>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
                        background: sc2.bg, color: sc2.c, border: `1px solid ${sc2.border}` }}>{c.statut}</span>
                    </div>
                    <p style={{ fontSize: 12, color: t.textMuted, margin: 0 }}>
                      <strong style={{ color: t.text }}>{fmt(c.montant_final)}/mois</strong>
                      {c.date_signature && ` · Signé le ${fmtDate(c.date_signature)}`}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: 7 }}>
                    <button onClick={() => superAdminApi.contrats.getPdf(c.id)}
                      style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px',
                        borderRadius: 9, background: t.bgInput, color: t.text, border: `1px solid ${t.border}`,
                        fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                      <Eye size={13} /> PDF
                    </button>
                    <button onClick={() => onViewContrat(c.id)}
                      style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px',
                        borderRadius: 9, background: t.primarySoft, color: t.primary,
                        border: `1px solid ${t.primaryBorder}`, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                      <ExternalLink size={13} /> Ouvrir
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* HISTORIQUE */}
        {onglet === 'historique' && (
          <div style={{ maxWidth: 600 }}>
            {/* Ajouter une note */}
            <div style={{ padding: '16px 18px', borderRadius: 14, background: t.bgCard,
              border: `1px solid ${t.border}`, marginBottom: 24 }}>
              <p style={{ fontSize: 13, fontWeight: 700, color: t.text, margin: '0 0 10px' }}>Ajouter une note</p>
              <textarea value={note} onChange={e => setNote(e.target.value)} rows={3}
                placeholder="Observation, retour prospect, prochaine action..."
                style={{ width: '100%', padding: '10px 14px', borderRadius: 10, background: t.bgInput,
                  border: `1.5px solid ${t.border}`, color: t.text, fontSize: 13, resize: 'vertical',
                  fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none', lineHeight: '1.5' }}
                onFocus={e => e.target.style.borderColor = t.primary}
                onBlur={e  => e.target.style.borderColor = t.border} />
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                <button onClick={handleAddNote} disabled={savingNote || !note.trim()}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 18px',
                    borderRadius: 9, background: t.primary, color: 'white', border: 'none',
                    fontSize: 12, fontWeight: 700, cursor: 'pointer',
                    opacity: (!note.trim() || savingNote) ? 0.5 : 1 }}>
                  <Save size={13} /> Enregistrer
                </button>
              </div>
            </div>

            {/* Timeline */}
            {actions.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', borderRadius: 16, background: t.bgCard, border: `2px dashed ${t.border}` }}>
                <History size={32} style={{ color: t.textMuted, opacity: 0.25, margin: '0 auto 10px', display: 'block' }} />
                <p style={{ fontSize: 13, color: t.textMuted }}>Aucune action enregistrée</p>
              </div>
            ) : (
              <div style={{ position: 'relative' }}>
                {/* Ligne verticale */}
                <div style={{ position: 'absolute', left: 19, top: 20, bottom: 0,
                  width: 2, background: t.border, zIndex: 0 }} />
                {actions.map((action, i) => {
                  const cfg = ACTION_CONFIG[action.type_action] || ACTION_CONFIG.note;
                  return (
                    <div key={action.id} style={{ display: 'flex', gap: 14, marginBottom: 16,
                      position: 'relative', zIndex: 1 }}>
                      {/* Dot */}
                      <div style={{ width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                        background: t.bgCard, border: `2px solid ${t.border}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 16, zIndex: 2 }}>
                        {cfg.icon}
                      </div>
                      {/* Contenu */}
                      <div style={{ flex: 1, paddingTop: 8 }}>
                        <div style={{ padding: '10px 14px', borderRadius: 12, background: t.bgCard,
                          border: `1px solid ${t.border}` }}>
                          <p style={{ fontSize: 13, fontWeight: 600, color: t.text, margin: '0 0 4px' }}>
                            {action.description}
                          </p>
                          <p style={{ fontSize: 11, color: t.textMuted, margin: 0 }}>
                            {fmtTime(action.created_at)}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
    </>
  );
};

// ============================================================================
// COMPOSANT PRINCIPAL — ProspectionHub
// ============================================================================
const ProspectionHub = ({ onCreateDevis, onViewContrat }) => {
  const t = useTheme();
  const [view,       setView]       = useState('pipeline');
  const [prospects,  setProspects]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [modal,      setModal]      = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [error,      setError]      = useState('');
  const [success,    setSuccess]    = useState('');
  const [search,     setSearch]     = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await superAdminApi.prospects.getAll();
      if (res.success) setProspects(res.data || []);
    } catch { setError('Erreur de chargement'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (form) => {
    const isEdit = modal && modal !== 'new';
    // Forcer statut 'nouveau' à la création
    if (!isEdit) form.statut = 'nouveau';
    const res = isEdit
      ? await superAdminApi.prospects.update(modal.id, form)
      : await superAdminApi.prospects.create(form);
    if (!res.success) throw new Error(res.message);
    setSuccess(isEdit ? 'Prospect mis à jour !' : 'Prospect créé !');
    setTimeout(() => setSuccess(''), 3000);
    setModal(null);
    await load();
  };

  const handleDelete = async (id, nom) => {
    if (!window.confirm(`Supprimer "${nom}" définitivement ?`)) return;
    await superAdminApi.prospects.delete(id);
    await load();
  };

  const filtered = prospects.filter(p =>
    !search ||
    p.nom.toLowerCase().includes(search.toLowerCase()) ||
    (p.contact || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.ville   || '').toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 80, gap: 12 }}>
      <RefreshCw size={20} style={{ color: t.primary, animation: 'spin 1s linear infinite' }} />
      <span style={{ fontSize: 13, color: t.textMuted }}>Chargement…</span>
    </div>
  );

  // Vue détail
  if (view === 'detail' && selectedId) return (
    <ProspectDetail
      prospectId={selectedId}
      theme={t}
      onBack={() => { setView('pipeline'); load(); }}
      onEdit={p => setModal(p)}
      onCreateDevis={p => onCreateDevis && onCreateDevis(p)}
      onViewContrat={id => onViewContrat && onViewContrat(id)}
      onReload={load}
    />
  );

  // Vue pipeline
  const total     = prospects.length;
  const convertis = prospects.filter(p => p.statut === 'converti').length;
  const enCours   = prospects.filter(p => !['converti', 'perdu'].includes(p.statut)).length;

  return (
    <div style={{ padding: '24px 28px 40px', display: 'flex', flexDirection: 'column', gap: 24, width: '100%', boxSizing: 'border-box' }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 900, color: t.text, margin: '0 0 6px', letterSpacing: '-0.5px' }}>
            Pipeline Prospection
          </h1>
          <div style={{ display: 'flex', gap: 16 }}>
            <span style={{ fontSize: 13, color: t.textMuted }}>
              <strong style={{ color: t.text }}>{total}</strong> prospect{total !== 1 ? 's' : ''}
            </span>
            <span style={{ fontSize: 13, color: '#059669' }}>
              <strong>{convertis}</strong> converti{convertis !== 1 ? 's' : ''}
            </span>
            <span style={{ fontSize: 13, color: t.primary }}>
              <strong>{enCours}</strong> en cours
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ position: 'relative' }}>
            <Users size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: t.textMuted }} />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Rechercher..."
              style={{ padding: '10px 14px 10px 36px', borderRadius: 12, background: t.bgCard,
                border: `1.5px solid ${t.border}`, color: t.text, fontSize: 13, outline: 'none', width: 230,
                transition: 'border-color 0.15s' }}
              onFocus={e => e.target.style.borderColor = t.primary}
              onBlur={e  => e.target.style.borderColor = t.border} />
          </div>
          <button onClick={() => setModal('new')}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px',
              borderRadius: 12, background: t.primary, color: 'white', border: 'none',
              fontSize: 13, fontWeight: 700, cursor: 'pointer',
              boxShadow: `0 4px 14px ${t.primary}40` }}>
            <Plus size={15} /> Nouveau prospect
          </button>
        </div>
      </div>

      {/* Alertes */}
      {(error || success) && (
        <div style={{ padding: '12px 16px', borderRadius: 12,
          background: success ? 'rgba(5,150,105,0.06)' : 'rgba(220,38,38,0.06)',
          border: `1px solid ${success ? 'rgba(5,150,105,0.2)' : 'rgba(220,38,38,0.2)'}`,
          display: 'flex', gap: 10, alignItems: 'center' }}>
          {success ? <CheckCircle2 size={15} style={{ color: '#059669' }} /> : <AlertCircle size={15} style={{ color: '#dc2626' }} />}
          <span style={{ fontSize: 13, color: success ? '#059669' : '#dc2626', fontWeight: 500 }}>{success || error}</span>
        </div>
      )}

      {/* ── Instructions pipeline ── */}
      <div style={{ padding: '14px 20px', borderRadius: 14,
        background: `linear-gradient(135deg, ${t.primarySoft || '#f0f9fb'}, ${t.bgCard})`,
        border: `1px solid ${t.primaryBorder || t.border}`,
        display: 'flex', alignItems: 'flex-start', gap: 14 }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, flexShrink: 0,
          background: t.primary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Zap size={16} style={{ color: 'white' }} />
        </div>
        <div style={{ flex: 1 }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: t.text, margin: '0 0 6px' }}>
            Comment fonctionne le pipeline ?
          </p>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            {[
              { step: '1', label: 'Créer le prospect',     color: '#64748b' },
              { step: '→', label: null },
              { step: '2', label: 'Envoyer les fiches',    color: '#2563eb' },
              { step: '→', label: null },
              { step: '3', label: 'Créer le devis',        color: '#d97706' },
              { step: '→', label: null },
              { step: '4', label: 'Devis accepté',         color: '#7c3aed' },
              { step: '→', label: null },
              { step: '5', label: 'Docs + Contrat',        color: '#059669' },
            ].map((item, i) => item.label === null ? (
              <span key={i} style={{ fontSize: 12, color: t.textMuted }}>→</span>
            ) : (
              <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 600,
                background: item.color + '12', color: item.color, border: `1px solid ${item.color}25` }}>
                <span style={{ width: 16, height: 16, borderRadius: '50%', background: item.color,
                  color: 'white', fontSize: 9, fontWeight: 800,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {item.step}
                </span>
                {item.label}
              </span>
            ))}
          </div>
          <p style={{ fontSize: 11, color: t.textMuted, margin: '6px 0 0' }}>
            Les statuts se mettent à jour automatiquement selon vos actions. Cliquez sur une carte pour voir le détail.
          </p>
        </div>
      </div>

      {/* ── Kanban ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 14, alignItems: 'start' }}>
        {STATUTS.map(s => {
          const col = filtered.filter(p => p.statut === s.value);
          return (
            <div key={s.value}>
              {/* En-tête colonne */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 12px', marginBottom: 10, borderRadius: 10,
                background: s.bg, border: `1px solid ${s.border}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.dot, flexShrink: 0 }} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: s.color }}>{s.label}</span>
                </div>
                <span style={{ fontSize: 11, fontWeight: 800, color: s.color,
                  background: 'rgba(255,255,255,0.7)', padding: '1px 6px', borderRadius: 6 }}>
                  {col.length}
                </span>
              </div>

              {/* Cartes */}
              {col.length === 0 ? (
                <div style={{ padding: '20px 12px', textAlign: 'center',
                  borderRadius: 10, border: `1.5px dashed ${s.border}` }}>
                  <p style={{ fontSize: 10, color: s.color, opacity: 0.4, margin: 0 }}>Vide</p>
                </div>
              ) : (
                col.map(p => (
                  <KanbanCard key={p.id} prospect={p} theme={t}
                    onClick={() => { setSelectedId(p.id); setView('detail'); }} />
                ))
              )}
            </div>
          );
        })}
      </div>

      {/* Modal */}
      {modal && (
        <ProspectModal
          prospect={modal === 'new' ? null : modal}
          onSave={handleSave}
          onClose={() => setModal(null)}
          theme={t}
        />
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }
      `}</style>
    </div>
  );
};

export default ProspectionHub;
